import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { useCurrency } from '../../context/CurrencyContext';
import { F, MONO_FEAT, EASE, withAlpha } from '../../styles/theme';
import { fmtC, fmtEur } from '../../utils/format';
import CoinImage from '../CoinImage';
import TierBadge from './TierBadge';
import BrandChevron from './BrandChevron';

// ── Freigabe-Agenda (Portfolio-Überblick) ────────────────────────────────────
// Die Überblick-Ebene über dem per-Coin Smart Check: alle offenen FIFO-Tranchen
// chronologisch danach sortiert, wann sie die 1-Jahres-Frist überschreiten.
// Daten kommen fertig aus buildReleaseAgenda (releaseAgenda.js) — hier nur Anzeige.
// Jede Zeile ist klickbar → setzt den Coin (Ebene 2: FullTimeline).
//
// Calm-konform: flach, keine getönten Container/Glows (feedback_no_pills_borders_glow),
// Rot nur als Punkt-Akzent, Mono-Zahlen, „frei" = T.green / „gebunden" = T.loss.

function fmtDateShort(ms, locale) {
  return new Date(ms).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// Tranchen in Zeit-Eimer gruppieren: „Bald" (≤90 T) → Jahr → Jahr.
// Reihenfolge folgt den (bereits chronologisch sortierten) Ereignissen.
function bucketize(events, today, t) {
  const todayYear = today.getUTCFullYear();
  const order = [];
  const byKey = new Map();
  for (const ev of events) {
    let key, label;
    if (ev.days != null && ev.days <= 90) {
      key = 'soon';
      label = t('red_ag_bucket_soon');
    } else {
      const y = new Date(ev.freeMs).getUTCFullYear();
      key = `y${y}`;
      label = y === todayYear ? t('red_ag_bucket_later_year', { year: y }) : String(y);
    }
    if (!byKey.has(key)) {
      const g = { key, label, events: [] };
      byKey.set(key, g);
      order.push(g);
    }
    byKey.get(key).events.push(ev);
  }
  return order;
}

// Nahe Termine teilen sich einen Achsen-Punkt (sonst unsichtbare Überlappung
// bei 365 Tagen Spannweite). Schwelle ~3,5% der Achse ≈ 13 Tage; Position des
// Clusters = Mittel seiner Mitglieder.
function clusterAxisEvents(events) {
  const sorted = [...events]
    .map((ev) => ({ ev, pct: Math.max(0, Math.min(100, ((ev.days ?? 0) / 365) * 100)) }))
    .sort((a, b) => a.pct - b.pct);
  const clusters = [];
  for (const { ev, pct } of sorted) {
    const last = clusters[clusters.length - 1];
    if (last && pct - last.pct <= 3.5) {
      last.events.push(ev);
      last.sumPct += pct;
      last.pct = last.sumPct / last.events.length;
    } else {
      clusters.push({ pct, sumPct: pct, events: [ev] });
    }
  }
  return clusters;
}

// Ein Achsen-Punkt: 26px-Hover-Fläche um eine sichtbare Kugel (wächst leicht
// mit Cluster-Größe), Hover = Vergrößern + Tooltip mit den Tranchen.
// Bewusst NICHT klickbar — reine Lese-Ebene, Navigation läuft über die Liste.
function AxisDot({ cluster, daysUnit, appearDelay = 0 }) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();
  const [hover, setHover] = useState(false);

  const n = cluster.events.length;
  const size = Math.min(11 + (n - 1) * 2, 17);
  const align = cluster.pct < 10 ? 'left' : cluster.pct > 90 ? 'right' : 'center';

  return (
    <motion.div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ scale: 0.3, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 460, damping: 28, delay: appearDelay }}
      style={{
        position: 'absolute', bottom: 11, left: `${cluster.pct}%`,
        x: '-50%', y: '50%',
        width: 26, height: 26, padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'default',
        zIndex: hover ? 3 : 1, WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{
        position: 'relative', width: size, height: size, borderRadius: '50%',
        background: T.gain || T.green, border: `2px solid ${T.bg}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: hover ? 'scale(1.3)' : 'scale(1)',
        transition: `transform .18s ${EASE}`,
        boxShadow: hover ? `0 2px 8px ${withAlpha(T.text, 0.2)}` : 'none',
      }}>
        {n > 1 && (
          <span style={{
            ...MONO_FEAT, fontFamily: F.num || F.mono,
            fontSize: 8.5, fontWeight: 800, color: T.bg, lineHeight: 1,
          }}>{n}</span>
        )}
      </span>

      <AnimatePresence>
      {hover && (
        <motion.div
          initial={{ opacity: 0, y: 4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 2, scale: 0.98 }}
          transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
          style={{
          position: 'absolute', bottom: 'calc(100% + 7px)',
          left: align === 'left' ? -4 : align === 'center' ? '50%' : 'auto',
          right: align === 'right' ? -4 : 'auto',
          x: align === 'center' ? '-50%' : 0,
          transformOrigin: align === 'left' ? 'bottom left' : align === 'right' ? 'bottom right' : 'bottom center',
          background: T.bg, border: `1px solid ${T.line}`, borderRadius: 10,
          padding: '7px 11px', whiteSpace: 'nowrap', textAlign: 'left',
          boxShadow: `0 8px 24px ${withAlpha('#000', 0.14)}`,
          pointerEvents: 'none',
        }}>
          {cluster.events.map((ev) => (
            <div key={`${ev.sym}-${ev.freeDate}`} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 0' }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.text }}>{ev.sym}</span>
              <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 11, color: T.sub }}>
                {ev.days} {daysUnit}
              </span>
              {ev.hasPrice && ev.valueEur != null && (
                <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 11, fontWeight: 700, color: T.text, marginLeft: 'auto' }}>
                  ≈ {fmtEur(convert(ev.valueEur), locale)} {symbol}
                </span>
              )}
            </div>
          ))}
        </motion.div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

// Eine Reife-Zeile. Klick → onPick(sym) (Ebene 2). Reife-Balken (Kauf → Frist)
// als Signatur aus TaxTimeline; rechts €-Näherung + Frei-Datum.
function AgendaRow({ ev, today, onPick, daysUnit, marketData }) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();

  const buyMs = +new Date(ev.buyDate);
  const span = ev.freeMs - buyMs;
  const heldPct = span > 0 ? Math.max(4, Math.min(100, ((+today - buyMs) / span) * 100)) : 100;

  return (
    <button
      type="button"
      onClick={() => onPick?.(ev.sym)}
      className="mo-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: '13px 10px', margin: '0 -10px',
        background: 'transparent', border: 'none',
        borderBottom: `1px solid ${T.lineSoft || T.line}`,
        borderRadius: 11, cursor: 'pointer', textAlign: 'left',
        color: T.text, WebkitTapHighlightColor: 'transparent',
        transition: 'background .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = withAlpha(T.text, 0.04); }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Countdown */}
      <div style={{ width: 60, flexShrink: 0, textAlign: 'right' }}>
        <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 17, fontWeight: 700, color: T.text, lineHeight: 1 }}>
          {ev.days}
        </div>
        <div style={{ fontSize: 10, fontWeight: 600, color: T.mute, marginTop: 2 }}>{daysUnit}</div>
      </div>

      {/* Avatar */}
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>
        <CoinImage sym={ev.sym} marketData={marketData} size={30} />
      </span>

      {/* Menge + Reife-Balken */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em' }}>
          {fmtC(ev.amount)} <span style={{ color: T.sub, fontWeight: 600 }}>{ev.sym}</span>
        </div>
        <div style={{ position: 'relative', height: 5, borderRadius: 3, background: withAlpha(T.text, 0.07), marginTop: 7, maxWidth: 230 }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${heldPct}%`, borderRadius: 3, background: T.loss || T.red }} />
          <div style={{ position: 'absolute', right: 0, top: -2, width: 2, height: 9, borderRadius: 1, background: T.gain || T.green }} />
        </div>
      </div>

      {/* €-Näherung + Frei-Datum */}
      <div style={{ flexShrink: 0, textAlign: 'right', width: 104 }}>
        {ev.hasPrice && ev.valueEur != null ? (
          <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', color: T.text }}>
            ≈ {fmtEur(convert(ev.valueEur), locale)} {symbol}
          </div>
        ) : (
          <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 14.5, fontWeight: 700, color: T.mute }}>—</div>
        )}
        <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 10.5, color: T.mute, marginTop: 2 }}>
          {fmtDateShort(ev.freeMs, locale)}
        </div>
      </div>
    </button>
  );
}

export default function ReleaseAgenda({ agenda, onPickCoin, marketData, onReminder, today = new Date() }) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();

  const buckets = useMemo(() => bucketize(agenda?.events || [], today, t), [agenda, today, t]);
  const axisClusters = useMemo(() => clusterAxisEvents(agenda?.events || []), [agenda]);
  const [axisOpen, setAxisOpen] = useState(false);
  // Während Auf-/Zuklapp-Animation muss overflow:hidden clippen; erst wenn die
  // Achse steht, wird er freigegeben (Tooltips ragen nach oben hinaus).
  const [axisSettled, setAxisSettled] = useState(false);
  const toggleAxis = () => { setAxisSettled(false); setAxisOpen((o) => !o); };
  const daysUnit = t('red_ag_days_unit');

  // Monats-Ticks für die 12-Monats-Achse (+3/+6/+9 Monate ab heute).
  const axisTicks = [3, 6, 9].map((m) => {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + m, 1));
    return { pct: (m / 12) * 100, label: d.toLocaleDateString(locale, { month: 'short' }) };
  });

  if (!agenda) return null;

  const freePctR = agenda.freePct != null ? Math.round(agenda.freePct) : null;
  const allFree = agenda.eventCount === 0;

  return (
    <div style={{ color: T.text, fontFamily: F.family }}>
      {/* Hero: großes Frei-% + Marktwert rechts */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 22, flexWrap: 'wrap' }}>
        {freePctR != null && (
          <div style={{
            fontFamily: F.display, fontSize: 64, fontWeight: 800,
            letterSpacing: '-0.05em', lineHeight: 0.86, color: T.gain || T.green,
          }}>
            {freePctR}<span style={{ fontSize: 30, fontWeight: 700, marginLeft: 2 }}>%</span>
          </div>
        )}
        <div style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.4, paddingTop: 8, maxWidth: 220 }}>
          {t('red_ag_hero_caption')}
          {agenda.hasPricedHoldings && (
            <div style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, color: T.text, fontWeight: 700, fontSize: 14, marginTop: 6 }}>
              ≈ {fmtEur(convert(agenda.freeValueEur), locale)} {symbol}
            </div>
          )}
        </div>
        {agenda.hasPricedHoldings && (
          <div style={{ marginLeft: 'auto', textAlign: 'right', paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.mute, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              {t('tax_market_value')}
            </div>
            <div style={{ ...MONO_FEAT, fontFamily: F.display, fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', color: T.text, lineHeight: 1 }}>
              {fmtEur(convert(agenda.totalValueEur), locale)} <span style={{ fontSize: 12, color: T.mute }}>{symbol}</span>
            </div>
          </div>
        )}
      </div>

      {allFree ? (
        <div style={{ padding: '28px 4px', fontSize: 14, color: T.sub, borderTop: `1px solid ${T.line}` }}>
          {t('red_ag_all_free')}
        </div>
      ) : (
        <>
          {/* 12-Monats-Achse — eingeklappt hinter einem schmalen Chevron-Streifen.
              Punkte geclustert (nahe Termine teilen sich eine Kugel) + Hover-Tooltip,
              Klick auf eine Kugel springt zum Coin. */}
          <div style={{ margin: '0 0 8px' }}>
            <button
              type="button"
              onClick={toggleAxis}
              aria-expanded={axisOpen}
              aria-label={t('red_ag_axis_toggle')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', padding: '5px 0', margin: 0,
                background: 'transparent', border: 'none', borderRadius: 9,
                cursor: 'pointer', color: T.mute,
                WebkitTapHighlightColor: 'transparent', transition: 'background .15s, color .15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = withAlpha(T.text, 0.04); e.currentTarget.style.color = T.sub; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.mute; }}
            >
              <span style={{
                display: 'inline-flex',
                transform: axisOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: `transform .3s ${EASE}`,
              }}>
                <BrandChevron dir="down" size={15} sw={2.2} />
              </span>
            </button>

            {/* Höhe wird MIT animiert (kein Layout-Sprung der Liste darunter);
                overflow erst nach dem Aufklappen freigeben, damit die
                Hover-Tooltips über den Container hinausragen dürfen. */}
            <AnimatePresence initial={false}>
              {axisOpen && (
                <motion.div
                  key="axis"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.42, ease: [0.16, 1, 0.3, 1] },
                    opacity: { duration: 0.26, ease: 'easeOut' },
                  }}
                  onAnimationComplete={() => { if (axisOpen) setAxisSettled(true); }}
                  style={{ overflow: axisSettled ? 'visible' : 'hidden' }}
                >
                  {/* Padding statt Margin: Margins würden beim Freigeben von
                      overflow aus dem Container kollabieren → Höhen-Sprung. */}
                  <div style={{ padding: '14px 0 10px' }}>
                  <div style={{ position: 'relative', height: 44 }}>
                    {/* Grundlinie */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 14, height: 1, background: T.line }} />
                    {/* Heute-Marke */}
                    <div style={{ position: 'absolute', bottom: 8, left: 0, width: 1, height: 14, background: T.sub, opacity: 0.6 }} />
                    <div style={{
                      position: 'absolute', bottom: -6, left: 0,
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: T.sub,
                    }}>{t('red_tl_today')}</div>
                    {/* Monats-Ticks */}
                    {axisTicks.map((tk) => (
                      <div key={tk.pct}>
                        <div style={{ position: 'absolute', bottom: 11, left: `${tk.pct}%`, width: 1, height: 6, background: T.lineSoft || T.line }} />
                        <div style={{
                          position: 'absolute', bottom: -6, left: `${tk.pct}%`, transform: 'translateX(-50%)',
                          fontSize: 9, color: T.mute, fontWeight: 600,
                        }}>{tk.label}</div>
                      </div>
                    ))}
                    {/* Ereignis-Kugeln (geclustert, grün = Freigabe-Zeitpunkt) —
                        setzen gestaffelt von links nach rechts ein */}
                    {axisClusters.map((cl, i) => (
                      <AxisDot
                        key={`${cl.events[0].sym}-${cl.events[0].freeDate}`}
                        cluster={cl}
                        daysUnit={daysUnit}
                        appearDelay={0.12 + i * 0.04}
                      />
                    ))}
                  </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {buckets.map((g) => (
            <div key={g.key}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
                color: T.mute, margin: '24px 0 4px',
              }}>{g.label}</div>
              {g.events.map((ev) => (
                <AgendaRow key={`${ev.sym}-${ev.freeDate}`} ev={ev} today={today} onPick={onPickCoin} daysUnit={daysUnit} marketData={marketData} />
              ))}
            </div>
          ))}

          {/* „noch gebunden"-Abschluss + Legende */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.line}` }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: T.loss || T.red, flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, color: T.sub }}>
              {agenda.hasPricedHoldings && (
                <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, color: T.text, fontWeight: 700 }}>
                  {fmtEur(convert(agenda.boundValueEur), locale)} {symbol}
                </span>
              )}{' '}{t('red_ag_bound_left')}
            </span>
          </div>

          {/* Premium-Reminder — öffnet den Alert-Wizard mit vorgewähltem
              Haltefrist-Typ. Gating (Premium) übernimmt der Wizard/Server. */}
          {onReminder && (
            <button
              type="button"
              onClick={onReminder}
              className="mo-row"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                marginTop: 16, padding: '13px 15px',
                background: 'transparent', border: `1px solid ${T.line}`, borderRadius: 14,
                cursor: 'pointer', textAlign: 'left', color: T.text,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg className="mo-lead" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={T.sub} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em' }}>
                  {t('red_ag_reminder')}
                </span>
                <span style={{ display: 'block', fontSize: 11.5, color: T.mute, marginTop: 1 }}>
                  {t('red_ag_reminder_sub')}
                </span>
              </span>
              <TierBadge tier="premium" scale={0.85} />
            </button>
          )}
        </>
      )}
    </div>
  );
}
